import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { TypographyProps } from "@mui/material/Typography";
import { useMediaQuery } from "@mui/material";

type NiyaazShowFieldProps = {
  label: string;
  value: ReactNode;
  size?: { xs?: number; sm?: number; md?: number };
  helperText?: string;
};

export type NiyaazShowLabeledItem = {
  label: string;
  value: ReactNode;
  valueVariant?: TypographyProps["variant"];
  valueFontWeight?: number;
  valueColor?: string;
};

type NiyaazShowLabeledStackProps = {
  items: NiyaazShowLabeledItem[];
  /** On xs, spread items in a row (matches legacy SimpleShowLayout). */
  rowOnMobile?: boolean;
};

export const NiyaazShowYesNo = (value: boolean | undefined | null) => (value ? "Yes" : "No");

export const NiyaazShowLabeledRow = ({
  label,
  value,
  valueVariant = "body1",
  valueFontWeight,
  valueColor,
}: NiyaazShowLabeledItem) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
      {label}
    </Typography>
    <Typography
      variant={valueVariant}
      component="div"
      fontWeight={valueFontWeight}
      color={valueColor}
      sx={{ wordBreak: "break-word" }}
    >
      {value ?? "—"}
    </Typography>
  </Box>
);

export const NiyaazShowLabeledStack = ({
  items,
  rowOnMobile = false,
}: NiyaazShowLabeledStackProps) => {
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"), { noSsr: true });
  const direction = rowOnMobile && isSmall ? "row" : "column";

  return (
    <Stack
      direction={direction}
      spacing={direction === "row" ? 2 : 1.75}
      flexWrap={direction === "row" ? "wrap" : undefined}
      justifyContent={direction === "row" ? "space-between" : undefined}
      useFlexGap
    >
      {items.map((item) => (
        <NiyaazShowLabeledRow key={item.label} {...item} />
      ))}
    </Stack>
  );
};

export const NiyaazShowField = ({
  label,
  value,
  size = { xs: 12, sm: 6 },
  helperText,
}: NiyaazShowFieldProps) => (
  <Grid size={size}>
    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
      {label}
    </Typography>
    <Typography variant="body1" component="div" sx={{ wordBreak: "break-word" }}>
      {value ?? "—"}
    </Typography>
    {helperText ? (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {helperText}
      </Typography>
    ) : null}
  </Grid>
);
